import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';
import { tokenStore } from './token-store';
import { rateLimitBudget } from './rate-limiter';
import { recordLatencySample } from '@/shared/lib/latency-buffer';

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: { start: number };
  }
}

/** Shared mutex to prevent concurrent token refresh calls */
let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function onRefreshSuccess(token: string) {
  refreshQueue.forEach((p) => p.resolve(token));
  refreshQueue = [];
}

function onRefreshFailure(error: unknown) {
  refreshQueue.forEach((p) => p.reject(error));
  refreshQueue = [];
}

/** Normalize pagination: backend uses both `total` and `total_count` */
function normalizePagination(data: unknown): unknown {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const d = data as Record<string, unknown>;
    if ('total_count' in d && !('total' in d)) {
      return { ...d, total: d['total_count'] };
    }
  }
  return data;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000';

export const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30_000,
});

// ── Request interceptor: attach Bearer token + correlation ID ──────────────
apiClient.interceptors.request.use((config) => {
  const token = tokenStore.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Stamp request start so the response interceptor can record round-trip
  // latency for the StatusBar p50 indicator (LOK-1097).
  config.metadata = { start: performance.now() };

  // Attach a unique correlation ID for request tracing
  const correlationId = crypto.randomUUID();
  config.headers['X-Correlation-ID'] = correlationId;

  // Tag Sentry breadcrumb so errors can be correlated with backend logs
  Sentry.addBreadcrumb({
    category: 'http',
    message: `${config.method?.toUpperCase()} ${config.url}`,
    data: { correlation_id: correlationId },
    level: 'info',
  });

  return config;
});

// ── Response interceptor: normalize pagination + handle 401 ──────────────
apiClient.interceptors.response.use(
  (response) => {
    rateLimitBudget.record();
    const start = response.config.metadata?.start;
    if (start !== undefined) recordLatencySample(performance.now() - start);
    response.data = normalizePagination(response.data);
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    const start = error.config?.metadata?.start;
    if (start !== undefined) recordLatencySample(performance.now() - start);

    // Handle 429 rate limiting
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'];
      const seconds = retryAfter ? parseInt(String(retryAfter), 10) : 60;
      rateLimitBudget.onRateLimited(seconds);
      const quotaLimitHeader = error.response.headers['x-quota-limit'];
      const quotaLimit = quotaLimitHeader ? parseInt(String(quotaLimitHeader), 10) : undefined;
      throw Object.assign(error, {
        retryAfterSeconds: seconds,
        ...(quotaLimit !== undefined && !isNaN(quotaLimit) && { quotaLimit }),
      });
    }

    // Handle 403 with MFA requirement — prompt user for TOTP code and retry
    if (error.response?.status === 403) {
      const detail = error.response.data as { detail?: string; mfa_required?: boolean };
      if (detail?.mfa_required) {
        // Dynamic import avoids circular dep: client.ts → features/auth
        const { useMfaChallengeStore } = await import('@/features/auth/store/mfa-challenge-store');
        return new Promise((resolve, reject) => {
          useMfaChallengeStore.getState().open({
            resolve: (mfaCode: string) => {
              if (originalRequest.headers) {
                (originalRequest.headers as Record<string, string>)['X-MFA-Code'] = mfaCode;
              }
              resolve(apiClient(originalRequest));
            },
            reject,
          });
        });
      }
    }

    // Handle 401: attempt token refresh (only once per request)
    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = tokenStore.getRefreshToken();
      if (!refreshToken) {
        redirectToLogin();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise((resolve, reject) => {
          refreshQueue.push({
            resolve: (token: string) => {
              if (originalRequest.headers) {
                (originalRequest.headers as Record<string, string>)['Authorization'] =
                  `Bearer ${token}`;
              }
              resolve(apiClient(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post<{
          access_token: string;
          refresh_token: string;
        }>(
          `${API_BASE}/api/v1/auth/refresh`,
          { refresh_token: refreshToken },
          { headers: { 'Content-Type': 'application/json' } },
        );

        tokenStore.setAccessToken(data.access_token);
        // Single-use refresh token rotation: backend ALWAYS returns a new refresh_token.
        // The old one is immediately revoked (family revocation on reuse).
        tokenStore.setRefreshToken(data.refresh_token);
        onRefreshSuccess(data.access_token);

        if (originalRequest.headers) {
          (originalRequest.headers as Record<string, string>)['Authorization'] =
            `Bearer ${data.access_token}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        onRefreshFailure(refreshError);
        tokenStore.clear();
        // Distinguish refresh-401 (token family revoked) from generic session expiry
        const isSecurityRevocation =
          refreshError instanceof Error &&
          'response' in refreshError &&
          (refreshError as AxiosError).response?.status === 401;
        if (isSecurityRevocation) {
          Sentry.addBreadcrumb({
            category: 'auth',
            message: 'Session terminated: refresh token family revoked',
            level: 'warning',
          });
        }
        redirectToLogin(isSecurityRevocation ? 'security' : undefined);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

function redirectToLogin(reason?: 'security'): void {
  tokenStore.notifyAuthExpired(reason);
}
